import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../../../api/api';
import { toast } from 'react-toastify';
import countriesData from '../../../Config/countries.json';

const VALID_COUNTRY_CODES = new Set(countriesData.countries.map(c => c.value.toLowerCase()));
const countryPrefixes = {};
countriesData.countries.forEach(c => {
    if (c.phoneCode && c.value) {
        countryPrefixes[c.phoneCode] = c.value.toLowerCase();
    }
});
countryPrefixes["1"] = "us";

export const useSMS = (t) => {
    const dispatch = useDispatch();
    const userCache = useSelector(state => state.sms?.userCache || {});

    const [balance, setBalance] = useState('0.00');
    const [loadingBalance, setLoadingBalance] = useState(false);

    const [historyMessages, setHistoryMessages] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [bills, setBills] = useState([]);
    const [loadingBills, setLoadingBills] = useState(false);

    const [activePhone, setActivePhone] = useState(null);
    const [sending, setSending] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [limit, setLimit] = useState(150);
    const [countryFilter, setCountryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [loadingUserInfo, setLoadingUserInfo] = useState(false);

    // Fetch account balance
    const fetchBalance = useCallback(async () => {
        setLoadingBalance(true);
        try {
            const resp = await api.get('/sms/balance');
            if (resp.data && resp.data.balance !== undefined) {
                setBalance(resp.data.balance);
            }
        } catch (err) {
            console.error('Error fetching SMS balance:', err);
        } finally {
            setLoadingBalance(false);
        }
    }, []);

    // Fetch SMS history (e.g. up to limit items to get a good grouping of threads)
    const fetchHistory = useCallback(async (showLoading = true) => {
        if (showLoading) setLoadingHistory(true);
        try {
            // Convert dates to Unix timestamps for ClickSend if set
            let url = `/sms/history?limit=${limit}`;
            if (dateFrom) {
                const tsFrom = Math.floor(new Date(dateFrom).getTime() / 1000);
                url += `&dateFrom=${tsFrom}`;
            }
            if (dateTo) {
                // end of day for dateTo
                const endTo = new Date(dateTo);
                endTo.setHours(23, 59, 59, 999);
                const tsTo = Math.floor(endTo.getTime() / 1000);
                url += `&dateTo=${tsTo}`;
            }
            if (searchQuery && !/^\d+$/.test(searchQuery.trim())) {
                // If search query is non-numeric, pass it as search content filter
                url += `&search=${encodeURIComponent(searchQuery.trim())}`;
            }

            const resp = await api.get(url);
            const dataObj = resp.data?.data || resp.data || {};
            const list = dataObj.data || [];
            
            if (Array.isArray(list)) {
                setHistoryMessages(list);
            }
        } catch (err) {
            console.error('Error fetching SMS history:', err);
        } finally {
            if (showLoading) setLoadingHistory(false);
        }
    }, [dateFrom, dateTo, searchQuery, limit]);

    // Fetch recharge transactions
    const fetchBills = useCallback(async () => {
        setLoadingBills(true);
        try {
            const resp = await api.get('/sms/bills?limit=50');
            const dataObj = resp.data?.data || resp.data || {};
            const list = dataObj.data || [];
            if (Array.isArray(list)) {
                setBills(list);
            }
        } catch (err) {
            console.error('Error fetching bills:', err);
        } finally {
            setLoadingBills(false);
        }
    }, []);

    // Send SMS
    const sendSMSMessage = useCallback(async (toPhone, body) => {
        if (!toPhone || !body.trim()) return false;
        setSending(true);
        try {
            const resp = await api.post('/sms/send', {
                to: toPhone,
                body: body.trim()
            });

            if (resp.data && resp.data.success) {
                toast.success(t('Pages.SMS.successSend') || 'تم إرسال الرسالة بنجاح');
                // Optimistically append outgoing message to history
                const optimisticMsg = {
                    message_id: 'opt_' + Date.now(),
                    to: toPhone,
                    from: 'alarabeyya',
                    body: body.trim(),
                    date: Math.floor(Date.now() / 1000),
                    direction: 'out',
                    status: 'Sent',
                    cost: 0.00
                };
                setHistoryMessages(prev => [optimisticMsg, ...prev]);
                fetchBalance(); // Refresh balance
                return true;
            } else {
                toast.error(t('Pages.SMS.errorSend') || 'فشل إرسال الرسالة');
                return false;
            }
        } catch (err) {
            console.error('Error sending SMS:', err);
            toast.error(t('Pages.SMS.errorOccurred') || 'حدث خطأ أثناء الإرسال');
            return false;
        } finally {
            setSending(false);
        }
    }, [fetchBalance, t]);

    // Fetch DB system user details based on phone and save to Redux store
    const fetchUserInfo = useCallback(async (phone) => {
        if (!phone) return;
        setLoadingUserInfo(true);
        try {
            const resp = await api.get(`/sms/user-info?phone=${phone}`);
            dispatch({
                type: 'SMS_CACHE_USER_INFO',
                phone: phone,
                userInfo: resp.data
            });
        } catch (err) {
            console.error('Error fetching user info:', err);
            dispatch({
                type: 'SMS_CACHE_USER_INFO',
                phone: phone,
                userInfo: {
                    phone: phone,
                    firstName: phone,
                    lastName: '',
                    isWhatsAppOnly: true
                }
            });
        } finally {
            setLoadingUserInfo(false);
        }
    }, [dispatch]);

    const getCountryFromPhone = useCallback((phone) => {
        if (!phone) return null;
        const cleanPhone = phone.replace(/\D/g, '');
        const prefix = Object.keys(countryPrefixes)
            .sort((a, b) => b.length - a.length)
            .find(pref => cleanPhone.startsWith(pref));
        if (prefix) {
            const countryCode = countryPrefixes[prefix];
            return VALID_COUNTRY_CODES.has(countryCode) ? countryCode : null;
        }
        return null;
    }, []);

    // Initial load
    useEffect(() => {
        fetchBalance();
        fetchHistory();
    }, [fetchBalance, fetchHistory]);

    // Reload history when date filters or limit change
    useEffect(() => {
        fetchHistory();
    }, [dateFrom, dateTo, limit, fetchHistory]);

    // Auto load and cache user info when visible chatThreads changes
    const chatThreadsRaw = useMemo(() => {
        const groups = {};

        historyMessages.forEach(msg => {
            const contactRaw = msg.direction === 'in' ? msg.from : msg.to;
            if (!contactRaw) return;

            const phoneClean = contactRaw.replace(/\D/g, '');
            if (!phoneClean) return;

            if (!groups[phoneClean]) {
                groups[phoneClean] = {
                    id: phoneClean,
                    phone: phoneClean,
                    latestMessage: msg,
                    messages: [],
                    totalCount: 0,
                    totalCost: 0
                };
            }

            groups[phoneClean].messages.push(msg);

            const msgPrice = msg.message_price !== undefined ? msg.message_price : (msg.price !== undefined ? msg.price : (msg.cost !== undefined ? msg.cost : 0));
            const numericPrice = parseFloat(msgPrice) || 0;

            groups[phoneClean].totalCount += 1;
            groups[phoneClean].totalCost += numericPrice;

            if (msg.date > groups[phoneClean].latestMessage.date) {
                groups[phoneClean].latestMessage = msg;
            }
        });

        return Object.values(groups);
    }, [historyMessages]);

    // Fetch and cache in background
    useEffect(() => {
        chatThreadsRaw.forEach(thread => {
            const phone = thread.phone;
            if (phone && !userCache[phone]) {
                api.get(`/sms/user-info?phone=${phone}`)
                    .then(resp => {
                        dispatch({
                            type: 'SMS_CACHE_USER_INFO',
                            phone: phone,
                            userInfo: resp.data
                        });
                    })
                    .catch(() => {
                        dispatch({
                            type: 'SMS_CACHE_USER_INFO',
                            phone: phone,
                            userInfo: {
                                phone: phone,
                                firstName: phone,
                                lastName: '',
                                isWhatsAppOnly: true
                            }
                        });
                    });
            }
        });
    }, [chatThreadsRaw, userCache, dispatch]);

    // Grouping message history into Chat Threads with full client-side filtering
    const chatThreads = useMemo(() => {
        let threads = [...chatThreadsRaw];

        // Filter threads by searchQuery (number search)
        if (searchQuery && /^\d+$/.test(searchQuery.trim())) {
            const cleanQuery = searchQuery.trim();
            threads = threads.filter(t => t.phone.includes(cleanQuery));
        } else if (searchQuery) {
            // Filter threads by cached user name
            const q = searchQuery.toLowerCase().trim();
            threads = threads.filter(t => {
                const cachedUser = userCache[t.phone];
                if (cachedUser && !cachedUser.isWhatsAppOnly) {
                    const name = `${cachedUser.firstName} ${cachedUser.lastName}`.toLowerCase();
                    return name.includes(q);
                }
                return false;
            });
        }

        // Filter by Country
        if (countryFilter) {
            threads = threads.filter(t => {
                const code = getCountryFromPhone(t.phone);
                return code === countryFilter.toLowerCase();
            });
        }

        // Filter by Status
        if (statusFilter && statusFilter !== 'all') {
            threads = threads.filter(t => {
                return t.latestMessage && t.latestMessage.status === statusFilter;
            });
        }

        // Sort threads by latest message date (newest first)
        threads.sort((a, b) => b.latestMessage.date - a.latestMessage.date);

        return threads;
    }, [chatThreadsRaw, searchQuery, countryFilter, statusFilter, userCache, getCountryFromPhone]);

    // Active chat messages list (sorted oldest to newest for chronological chat flow)
    const activeChatMessages = useMemo(() => {
        if (!activePhone) return [];
        const thread = chatThreadsRaw.find(t => t.phone === activePhone);
        if (!thread) return [];

        return [...thread.messages].sort((a, b) => a.date - b.date);
    }, [chatThreadsRaw, activePhone]);

    // Get active user details from cache
    const activeUserInfo = useMemo(() => {
        if (!activePhone) return null;
        return userCache[activePhone] || null;
    }, [userCache, activePhone]);

    return {
        balance,
        loadingBalance,
        fetchBalance,
        historyMessages,
        loadingHistory,
        fetchHistory,
        bills,
        loadingBills,
        fetchBills,
        activePhone,
        setActivePhone,
        sending,
        sendSMSMessage,
        userInfo: activeUserInfo,
        loadingUserInfo,
        fetchUserInfo,
        searchQuery,
        setSearchQuery,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        limit,
        setLimit,
        countryFilter,
        setCountryFilter,
        statusFilter,
        setStatusFilter,
        chatThreads,
        activeChatMessages,
        userCache
    };
};
